import type { VerificationData } from "./usePIIVerification";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntroRoomData as useIntroRoomDataBase } from "../page-content/useIntroRoomData";
import { logger as loggerBase } from "../../../datadog";
import { useParams as useParamsBase } from "react-router-dom";
import { useSignConsents as useSignConsentsBase } from "./useSignConsents";
import type {
  ConsentType,
  RelationshipToClientUser,
  ClientUserParentOrGuardianInput,
  GuardianFormValues,
} from "@grow-therapy-team/seedling-components";

type UseRequiredAgreementsProps = {
  consentSigningToken?: string | null;
  flowId: string;
  onSigningSuccess: () => void;
  piiVerificationData: VerificationData | null;
  setAccountDataVisitorName: (name: string) => void;
};

type Dependencies = {
  logError: typeof loggerBase.error;
  useIntroRoomData: typeof useIntroRoomDataBase;
  useParams: typeof useParamsBase;
  useSignConsents: typeof useSignConsentsBase;
};

export type SignConsentsVariables = {
  clientUserShortId: string;
  consentTypes: ConsentType[];
  flowId: string;
  clientUserParentGuardianShortId?: string | null;
  clientUserParentOrGuardian?: ClientUserParentOrGuardianInput;
};

/**
 * Custom hook for managing the state, logic, and callbacks for the required
 * agreements review form.
 *
 * @param props - Primary props for the hook:
 * - consentSigningToken: The single-use auth token to use for signing the
 *   required agreements. This is only applicable if the client used PII
 *   verification instead of logging in.
 * - flowId: The current unique ID of the client's trial of the intro room flow.
 *   This is used to track the client's progress through the flow in Segment.
 * - onSigningSuccess: The callback to invoke when the required agreements
 *   are submitted, regardless of success or failure.
 * - piiVerificationData: The data from the PII verification form, if the client
 *   used PII verification instead of logging in.
 * - setAccountDataVisitorName: Callback to invoke with data-derived visitor
 *   name to aid in setting the visitor's name when entering the waiting room.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 * @returns The required agreements form state, the callback for submitting the
 * form, and other props for the required agreements UI.
 */
export function useRequiredAgreements(
  {
    consentSigningToken,
    flowId,
    onSigningSuccess,
    piiVerificationData,
    setAccountDataVisitorName,
  }: UseRequiredAgreementsProps,
  { logError, useIntroRoomData, useParams, useSignConsents }: Dependencies = {
    logError: loggerBase.error,
    useIntroRoomData: useIntroRoomDataBase,
    useParams: useParamsBase,
    useSignConsents: useSignConsentsBase,
  },
) {
  const { patientShortId } = useParams();

  const {
    data: {
      clientUserWithParentGuardianData,
      missingConsentsData,
      requiresAuthedIntroRoomData,
    },
  } = useIntroRoomData({ flowId });

  const isMinor =
    requiresAuthedIntroRoomData?.clientRequiresAuthedIntroRoom?.isMinor;
  const clientUserShortId =
    requiresAuthedIntroRoomData?.clientRequiresAuthedIntroRoom
      ?.clientUserShortId ?? "";

  // Missing consent types will come from the PII verification data if the
  // client is not logged in, otherwise we get them from the query data.
  const missingConsentTypes = useMemo(() => {
    return (
      piiVerificationData?.missingConsentTypes ??
      missingConsentsData?.clientUserMissingConsents?.missingConsentTypes.filter(
        (consentType) => consentType !== null,
      ) ??
      []
    );
  }, [
    piiVerificationData?.missingConsentTypes,
    missingConsentsData?.clientUserMissingConsents?.missingConsentTypes,
  ]);

  // Client legal name will come from the PII verification data if the client is
  // not logged in, otherwise we get it from the query data.
  const clientLegalName = useMemo(() => {
    return (
      piiVerificationData?.fullLegalName ??
      clientUserWithParentGuardianData?.clientUser?.fullLegalName ??
      ""
    );
  }, [
    piiVerificationData?.fullLegalName,
    clientUserWithParentGuardianData?.clientUser?.fullLegalName,
  ]);

  // Report the client's legal name to the parent hook so it can be used to set
  // the visitor name when entering the waiting room.
  useEffect(() => {
    if (clientLegalName) {
      setAccountDataVisitorName(clientLegalName);
    }
  }, [clientLegalName, setAccountDataVisitorName]);

  // Get the guardian short ID if one exists on file.
  const clientUserParentGuardianShortId = useMemo(() => {
    return (
      piiVerificationData?.clientUserParentGuardianShortId ??
      clientUserWithParentGuardianData?.clientUser?.clientUserParentGuardian
        ?.clientUserParentGuardianShortId ??
      null
    );
  }, [
    piiVerificationData?.clientUserParentGuardianShortId,
    clientUserWithParentGuardianData?.clientUser?.clientUserParentGuardian
      ?.clientUserParentGuardianShortId,
  ]);
  const hasParentGuardian = !!clientUserParentGuardianShortId;

  // We *should* be guaranteed to have all the data we need at this point,
  // unless this hook is not being used correctly. If we're missing data, we
  // definitely want to know about it.
  useEffect(
    function logMissingRequiredData() {
      if (
        !missingConsentTypes.length ||
        !clientLegalName ||
        !clientUserShortId ||
        isMinor === undefined
      ) {
        logError("Missing some required data for required agreements", {
          flowId,
          patientShortId,
          missingData: {
            consentTypes: !missingConsentTypes.length,
            clientLegalName: !clientLegalName,
            clientUserShortId: !clientUserShortId,
            isMinor: isMinor === undefined ? true : false,
          },
        });
      }
    },
    [
      clientLegalName,
      clientUserShortId,
      isMinor,
      flowId,
      logError,
      missingConsentTypes,
      patientShortId,
    ],
  );

  // Form state
  const [isChecked, setIsChecked] = useState(false);

  // Guardian form state (only used when hasParentGuardian is false)
  const [guardianFormFields, setGuardianFormFields] =
    useState<GuardianFormValues>({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      relationshipToClient: "",
    });

  // Track form validation state
  const [isFormValid, setIsFormValid] = useState(false);

  // Form submission
  const {
    signConsents,
    hasError: hasSubmissionError,
    isSubmitting,
  } = useSignConsents({
    consentSigningToken,
    onSigningSuccess,
  });

  // Submit is disabled if form is invalid OR there's a submission error
  const isSubmitDisabled = !isFormValid || hasSubmissionError;

  const submit = useCallback(async () => {
    if (!isChecked) return;

    const mutationVariables: SignConsentsVariables = {
      clientUserShortId,
      consentTypes: missingConsentTypes,
      flowId,
    };

    if (isMinor) {
      if (clientUserParentGuardianShortId) {
        mutationVariables.clientUserParentGuardianShortId =
          clientUserParentGuardianShortId;
      } else {
        mutationVariables.clientUserParentOrGuardian = {
          firstName: guardianFormFields.firstName,
          lastName: guardianFormFields.lastName,
          email: guardianFormFields.email,
          phone: guardianFormFields.phoneNumber,
          // casting here because relationship field starts as an empty string, but will be prevented from submitting on the frontend when empty
          relationshipToClientUser:
            guardianFormFields.relationshipToClient as RelationshipToClientUser,
        };
      }
    }

    await signConsents(mutationVariables);
  }, [
    flowId,
    isChecked,
    clientUserShortId,
    missingConsentTypes,
    signConsents,
    isMinor,
    clientUserParentGuardianShortId,
    guardianFormFields,
  ]);

  return {
    clientLegalName,
    consentTypes: missingConsentTypes,
    hasParentGuardian,
    hasSubmissionError,
    isMinor: isMinor ?? false,
    onInputChange: setIsChecked,
    isChecked,
    isSubmitting,
    isReadOnly: false,
    // Guardian form fields (only provided when isMinor and does not yet have a
    // parent/guardian on file)
    ...(isMinor && !hasParentGuardian
      ? {
          guardianFormFields,
          onGuardianFormFieldsChange: setGuardianFormFields,
        }
      : {}),
    onSubmit: submit,
    isSubmitDisabled,
    onValidationChange: setIsFormValid,
  };
}
